<template>
  <div class="base" :style="styleObject">
    <top-button />
    <div class="base__header">
      <app-header
        v-bind="headerPropsObject"
        :utm-source="myStoreStore.utmSource"
        :cookie-error="isCookieError"
      />
    </div>
    <div class="base__body">
      <slot v-if="isInitializedApp" />
    </div>
    <div
      class="base__footer"
      :class="{
        hidden: route.name === 'my-store-search-type',
      }"
    >
      <app-footer v-bind="footerLinkObject" />
    </div>
    <loading :loaded="rootStore.isContentLoaded" />
    <message-dialog
      :title="rootStore.alertMessage.title"
      :text="rootStore.alertMessage.body"
      type="alert"
      :shown="alertMessageDialogShown"
      class="base__message-dialog"
      @close="handleAlertMessageDialogClose"
    >
      <template #cross>
        <div class="base__cross">
          <cross color="black" @click="handleAlertMessageDialogClose" />
        </div>
      </template>
      <template #footer>
        <button-group>
          <template #firstButton>
            <function-button
              text="閉じる"
              button-color="light"
              class="base__dialog-button"
              @click="handleAlertMessageDialogClose"
            />
          </template>
        </button-group>
      </template>
    </message-dialog>
    <!-- <agreement-dialog
      :chain-name="rootStore.chainName"
      :shown="!!agreementText"
      :text="agreementText"
      @agreement="onAgreement"
      @close="onDisagree"
    /> -->
    <div class="base__background" :class="classObject(route.name)"></div>
  </div>
</template>

<script setup>
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
// import { apiError } from '@/plugins/error'
import Loading from "@/components/Loading";
import TopButton from "@/components/TopButton";
import smoothscroll from "smoothscroll-polyfill";

import { useStore } from "@/stores/index";
import { useChirashiStore } from "@/stores/chirashi";
import { useMyStoreStore } from "@/stores/myStore";
import { useRoute } from "vue-router";
import { watch, ref, computed } from "vue";
import { GETSHOP } from "@/composables/utility";

const rootStore = useStore();
const chirashiStore = useChirashiStore();
const myStoreStore = useMyStoreStore();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
// const gtm = useGtm()

const data = await import(`@/data/${runtimeConfig.public.chainKey}/config.js`);
const options = ref(data?.default);
const isInitializedApp = ref(false);
const viewHeight = ref(0);

const alertMessageDialogShown = computed(() => {
  return rootStore.messageDialogShown("ALERT");
});

const classObject = computed(() => {
  return function (role) {
    // const { _errored } = this.$nuxt.context
    return {
      // top: role === 'index',
      // 'top-no-content': role === 'index' && !_errored && this.topNoContent,
      chirashi: role === "chirashi",
      "content-id": role === "content-id",
      content: role === "content",
      "terms-of-service": role === "terms-of-service",
      "privacy-policy": role === "privacy-policy",
      faq: role === "faq",
      // 'point-card': role === 'point-card',
      // 'point-benefits':
      //   role === 'point-benefits' &&
      //   !_errored &&
      //   !this.pointBenefitsNoContent,
      // 'point-benefits-no-content':
      //   role === 'point-benefits' &&
      //   !_errored &&
      //   this.pointBenefitsNoContent,
      "my-store-search-type": role === "my-store-search-type",
      other: role !== "index" && role !== "content-id" && role !== "content",
      // error: _errored,
      // marui: !this.isFlyerView ? 'marui' : '',
      flyer: true,
      "chirashi-splash": role === "chirashi" && myStoreStore.myStores.length === 0,
    };
  };
});

const isFlyerView = computed(() => {
  return true;
});

const returnLink = computed(() => {
  return myStoreStore.activeMyStoreId !== null && myStoreStore.activeMyStoreId !== undefined
    ? myStoreStore.utmSource
      ? `/chirashi/?shop-id=${myStoreStore.activeMyStoreId}&utm_source=${myStoreStore.utmSource}`
      : `/chirashi/?shop-id=${myStoreStore.activeMyStoreId}`
    : `/my-store/search/`;
});

const headerPropsObject = computed(() => {
  return {
    chainType: runtimeConfig.public.chainKey,
    chirashi: rootStore.menuChirashi,
    pointBenefits: rootStore.menuPointBenefits,
    chainLogo: pcLogo.value,
    chainName: rootStore.chainName,
    returnLink: returnLink.value,
    utmSource: myStoreStore.utmSource,
    isViewerComponent: true,
    isContentLoaded: rootStore.isContentLoaded,
    flyerViewerItem: chirashiStore.item,
    flyerViewerDate: chirashiStore.date,
    chainName: runtimeConfig.public.chainName,
  };
});

const footerLinkObject = computed(() => {
  return {
    linkName: runtimeConfig.public.chainName,
    linkUrl: options?.value?.footerLink,
    menuKeyCard: rootStore.menuKeyCard,
    menuChirashi: rootStore.menuChirashi,
    menuPointBenefits: rootStore.menuPointBenefits,
    isContentLoaded: rootStore.isContentLoaded,
  };
});

const defaultLogoFile = 'pc-logo.png'
const needsConditionalLogo = Number(runtimeConfig.public.mediaId) === 24
const pcLogo = ref(needsConditionalLogo ? '' : runtimeConfig.public.chainPath + "images/" + runtimeConfig.public.chainKey + "/" + defaultLogoFile)

const activeShopName = computed(() => {
  const activeShop = GETSHOP(myStoreStore.activeMyStoreId, myStoreStore.stores)
  return activeShop?.shopName || ''
})

if (needsConditionalLogo) {
  watch(() => activeShopName.value, (shopName) => {
    if (!shopName) {
      pcLogo.value = ''
      return
    }
    const isFoodStyle = shopName?.startsWith('フードスタイル')
    const logoFile = isFoodStyle ? 'pc-food-style-logo.png' : defaultLogoFile
    pcLogo.value = runtimeConfig.public.chainPath + "images/" + runtimeConfig.public.chainKey + "/" + logoFile
  }, { immediate: true })
}

const styleObject = computed(() => {
  return {
    "--vh": viewHeight.value ? viewHeight.value : "100vh",
  };
});

const isCookieError = computed(() => {
  if (route.name === "iframe") {
    return false;
  } else {
    return !navigator.cookieEnabled;
  }
});

const handleAlertMessageDialogClose = () => {
  rootStore.$patch({ alertMessage: { title: "", body: "" } });
};

const openAlertMessageDialog = () => {
  rootStore.setMessageDialogs({ name: "ALERT", shown: true });
};

const closeAlertMessageDialog = () => {
  rootStore.setMessageDialogs({ name: "ALERT", shown: false });
};

// const sendGADataAppMenuClick = ({ value }) => {
//   const store = {
//     home: {
//       category: "ホーム",
//       action: "ホーム",
//     },
//     chirashi: {
//       category: "チラシ一覧",
//       action: "チラシ",
//     },
//     "point-benefits": {
//       category: "ポイント特典一覧/買ったらスタンプ一覧",
//       action: "ポイント特典/買ったらスタンプ",
//     },
//   };
//   const to = value === "home" ? "" : value;
//   const data = {
//     event: "ga-btn-click",
//     category: store[value].category,
//     action: `ヘッダー_${store[value].action}`,
//     label: `${runtimeConfig.public.chirashiUrl}${to}/`,
//   };
//   // gtm.trackEvent(data);
// };

// const sendGADataAppFooterMenuClick = ({ value }) => {
//   const store = {
//     home: {
//       category: "ホーム",
//       action: "ホーム",
//     },
//     chirashi: {
//       category: "チラシ一覧",
//       action: "チラシ",
//     },
//     "point-card": {
//       category: "ポイントカード",
//       action: "ポイントカード",
//     },
//     "point-benefits": {
//       category: "ポイント特典一覧/買ったらスタンプ一覧",
//       action: "ポイント特典/買ったらスタンプ",
//     },
//   };
//   const to = value === "home" ? "" : value;
//   const data = {
//     event: "ga-btn-click",
//     category: store[value].category,
//     action: `ヘッダー_${store[value].action}`,
//     label: `${runtimeConfig.public.chirashiUrl}${to}/`,
//   };
//   // gtm.trackEvent(data);
//   rootStore.$patch({ isHome: true });
// };

const onResize = () => {
  viewHeight.value = `${window.innerHeight}px`;
};

onMounted(() => {
  onResize();
  window.addEventListener("resize", onResize);
  window.addEventListener("popstate", () => {
    rootStore.$patch({ isHistoryBack: true });
  });
  smoothscroll.polyfill();
});

watch(
  rootStore.alertMessage,
  (newValue, oldValue) => {
    if (newValue.title && newValue.body) {
      openAlertMessageDialog();
    } else {
      closeAlertMessageDialog();
    }
  },
  { immediate: true }
);

// FETCH
try {
  await rootStore.initializeApp();
} catch (err) {
  throw showError({ data: err.data, statusCode: err.statusCode, fatal: true });
} finally {
  isInitializedApp.value = true;
}
</script>

<style lang="scss" scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
}

.button--green {
  display: inline-block;
  border-radius: 4px;
  border: 1px solid #3b8070;
  color: #3b8070;
  text-decoration: none;
  padding: 10px 30px;
}

.button--green:hover {
  color: $color-white;
  background-color: #3b8070;
}

.button--grey {
  display: inline-block;
  border-radius: 4px;
  border: 1px solid #35495e;
  color: #35495e;
  text-decoration: none;
  padding: 10px 30px;
  margin-left: 15px;
}

.button--grey:hover {
  color: $color-white;
  background-color: #35495e;
}

.base {
  display: flex;
  flex-direction: column;
  min-height: var(--vh);
  margin: 0 auto;
  position: relative;

  .base__header {
    top: 0;
    left: 0;
    z-index: 100;
    width: 100%;
    @media screen and (min-width: $tablet-breakpoint) {
      height: 8.9rem;
    }
  }

  .base__body {
    flex: 0 1 auto;

    @media screen and (min-width: $tablet-breakpoint) {
      flex: 1 1 auto;
    }

    > div {
      padding-top: 0;
    }
  }

  .base__footer {
    // z-index: 1;
    &.hidden {
      & > footer {
        height: 49px + env(safe-area-inset-bottom);
        ::v-deep ul {
          display: none;
        }
      }
      @media screen and (min-width: $tablet-breakpoint) {
        display: none;
      }
    }
  }

  // .base__background {
  //   &::before {
  //     content: '';
  //     height: 104.3rem;
  //     width: 104.3rem;
  //     background: $color-white;
  //     border-radius: 50%;
  //     position: absolute;
  //     top: -15.7rem;
  //     left: -14.8rem;
  //   }
  //   &::after {
  //     content: '';
  //     height: 104.3rem;
  //     width: 104.3rem;
  //     background: transparent;
  //     border: solid 3.2rem $color-white;
  //     border-radius: 50%;
  //     position: absolute;
  //     top: 82rem;
  //     left: 54.4rem;
  //   }
  // }

  &.top {
    .base__body > div {
      @media screen and (min-width: $tablet-breakpoint) {
        padding-top: 0;
      }
    }
    .base__header {
      background-color: $color-white;
      @media screen and (min-width: $tablet-breakpoint) {
        background-color: transparent;
      }
    }
  }
  &.content {
    background-color: #fffbf0;
    .base__header {
      background-color: $color-white;

      @media screen and (min-width: $tablet-breakpoint) {
        box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.16);
      }

      /* stylelint-disable-next-line selector-pseudo-element-no-unknown */
      .app-header::v-deep {
        @media screen and (min-width: $tablet-breakpoint) {
          height: 100px;
        }
        .app-header__logo {
          @media screen and (min-width: $tablet-breakpoint) {
            width: 120px;
            height: 100px;
          }
        }
      }
    }
    .base__body > div {
      @media screen and (min-width: $tablet-breakpoint) {
        padding-top: 100px;
      }
    }
    // 記事一覧ページ
    .base__footer {
      position: absolute;
      bottom: 0;
      width: 100%;
    }
  }

  &.content-id {
    // background-color: #fffbf0;
    // .base__body > div {
    //   @media screen and (min-width: $tablet-breakpoint) {
    //     padding-top: 120px;
    //   }
    // }
    .base__footer {
      background-color: $color-white;
    }
  }

  &.terms-of-service,
  &.privacy-policy,
  &.faq,
  &.point-card {
    .base__footer {
      bottom: 0;
      width: 100%;
    }
  }

  .base__background {
    overflow: hidden;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: -1;
    @media screen and (min-width: $tablet-breakpoint) {
      background-color: $color-bg;
    }
    &.my-store-search-type {
      background-color: $color-bg;
    }
    &.chirashi-splash,
    &.top-no-content,
    &.point-benefits-no-content {
      background-color: $color-bg;
      &::before {
        content: none !important;
      }
      &::after {
        content: none !important;
      }
    }
    &.chirashi-splash.flyer {
      background-color: $color-white;
    }
    &.top.marui,
    &.chirashi.marui,
    &.point-benefits.marui {
      @media screen and (min-width: $tablet-breakpoint) {
        &::before {
          content: "";
          display: block;
          height: 69rem;
          width: 69rem;
          background-color: $color-white;
          border-radius: 50%;
          position: absolute;
          top: -15.7rem;
          left: -14.8rem;
          z-index: -1;
        }
        &::after {
          content: "";
          display: block;
          height: 69rem;
          width: 69rem;
          background-color: $color-bg;
          border: solid 3.2rem $color-white;
          border-radius: 50%;
          position: absolute;
          top: 49.1rem;
          left: 31.1rem;
          z-index: -1;
        }
      }
      @media screen and (min-width: $tablet2-breakpoint) {
        &::before {
          height: 89.5rem;
          width: 89.5rem;
        }
        &::after {
          height: 89.5rem;
          width: 89.5rem;
          top: 68.4rem;
          left: 44.8rem;
        }
      }
      @media screen and (min-width: $pc-breakpoint) {
        &::before {
          height: 104.3rem;
          width: 104.3rem;
        }
        &::after {
          height: 104.3rem;
          width: 104.3rem;
          top: 82rem;
          left: 54.4rem;
        }
      }
    }
  }
  .base__message-dialog {
    @media screen and (min-width: $tablet-breakpoint) {
      ::v-deep .message-dialog__footer {
        display: none;
      }
    }
  }
  .base__message-dialog-alert {
    ::v-deep .message-dialog__header {
      color: $color-alert;
    }
  }
  .base__dialog-button {
    @media screen and (min-width: $tablet-breakpoint) {
      display: none;
    }
  }
  .base__cross {
    margin-bottom: 0.8rem;
    color: $color-main-text;
  }
}
</style>
